import { client } from '../../../../client';
import { AttendanceAPIRecord, AttendanceAPIResponse } from '../types/attendance.types';

// Type for data returned by API
interface ApiUserData {
  id: number;
  name: string;
}

interface ApiRecordData {
  userId: number;
  date: string;
  hoursWorked: number;
}

interface ApiResponse {
  users: ApiUserData[];
  records: ApiRecordData[];
}

// Convert frontend period types to API-supported range types
function mapPeriodToApiRange(
  period: 'current_month' | 'last_3_months' | 'last_6_months'
): 'week' | 'month' | '3months' | '6months' {
  switch (period) {
    case 'current_month':
      return 'month';
    case 'last_3_months':
      return '3months';
    case 'last_6_months':
      return '6months';
    default:
      return 'month';
  }
}

// Get attendance data from API for specified period and project
export async function fetchAttendanceData(
  period: 'current_month' | 'last_3_months' | 'last_6_months',
  projectId: number
): Promise<AttendanceAPIResponse> {
  try {
    const range = mapPeriodToApiRange(period);
    console.log(`Fetching attendance data: projectId=${projectId}, period=${period}, range=${range}`);

    // Call API with corresponding parameters
    const response = await client.get<ApiResponse>(`/projects/${projectId}/users-stats?range=${range}`);

    console.log(
      `API response received: ${response.data.users.length} users, ${response.data.records.length} records`
    );

    // Add missing fields according to AttendanceAPIResponse format
    const data = response.data;

    // Enrich data by adding status based on hoursWorked
    const records = data.records.map(
      (record: ApiRecordData) =>
        ({
          ...record,
          status: record.hoursWorked > 0 ? (record.hoursWorked >= 8 ? 'present' : 'remote') : 'absent',
        }) as AttendanceAPIRecord
    );

    // Find min and max dates for the period
    let periodStart, periodEnd;

    if (records.length === 0) {
      // If no records exist, use current month
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      periodStart = firstDayOfMonth.toISOString();
      periodEnd = lastDayOfMonth.toISOString();
    } else {
      const dates = records.map((record: AttendanceAPIRecord) => new Date(record.date));
      const startDate = new Date(Math.min(...dates.map((d: Date) => d.getTime())));
      const endDate = new Date(Math.max(...dates.map((d: Date) => d.getTime())));
      periodStart = startDate.toISOString();
      periodEnd = endDate.toISOString();
    }

    // Form response in AttendanceAPIResponse format
    return {
      users: data.users.map((user: ApiUserData) => ({
        id: user.id,
        name: user.name,
        department: '', // These fields are added for compatibility
        position: '', // with existing interface
      })),
      records,
      periodStart,
      periodEnd,
      periodType: period,
    };
  } catch (error) {
    console.error('Error fetching attendance data:', error);
    // In case of error, return empty data
    // Use current month for period definition
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      users: [],
      records: [],
      periodStart: firstDayOfMonth.toISOString(),
      periodEnd: lastDayOfMonth.toISOString(),
      periodType: period,
    };
  }
}
